import { LuGlobe } from '@react-icons/all-files/lu/LuGlobe'
import React, { useCallback, useMemo } from 'react'

import { SOURCE_SCOPE } from '@disco/constants/search'
import {
  selectSearchPage,
  setSourceScope,
} from '@disco/state-management/reducers/search-page'
import { hasActiveFilters, resolveSourceScope } from '@disco/utils/search-page'

import { IconButton, Tooltip } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

const TOOLTIP_CONTENT_OFF =
  'Click to include tracks from other opted-in DISCOs when you search.'
const TOOLTIP_CONTENT_ON =
  'Click to exclude tracks from other opted-in DISCOs when you search.'

/**
 * Icon toggle for the MultiDISCO Search source scope, surfaced directly in
 * the search input so it's visible whether the user is browsing, about to
 * search, or looking at results. Mirrors {@link resolveSourceScope} and the
 * toggle pattern used by the Source column filter dropdown so both controls
 * always agree on the current scope.
 */
export const MdsScopeToggle = (): React.ReactNode => {
  const dispatch = useDispatch()
  const { sourceScope, hasSubmittedSearch, filters } =
    useSelector(selectSearchPage)

  const hasSearched = useMemo(
    () => hasSubmittedSearch || hasActiveFilters(filters),
    [hasSubmittedSearch, filters],
  )

  const displayedScopes = useMemo(
    () => resolveSourceScope(sourceScope, hasSearched),
    [sourceScope, hasSearched],
  )

  const mdsOn = displayedScopes.includes(SOURCE_SCOPE.MDS)

  // BUSINESS is always kept explicit — this control only ever adds/removes
  // MDS and must never be able to produce an empty or business-less scope,
  // even if the resolved scope was narrowed to MDS-only via the column filter.
  const handleClick = useCallback(() => {
    dispatch(
      setSourceScope(
        mdsOn
          ? [SOURCE_SCOPE.BUSINESS]
          : [SOURCE_SCOPE.BUSINESS, SOURCE_SCOPE.MDS],
      ),
    )
  }, [dispatch, mdsOn])

  return (
    <Tooltip content={mdsOn ? TOOLTIP_CONTENT_ON : TOOLTIP_CONTENT_OFF}>
      <IconButton
        variant="ghost"
        color={mdsOn ? 'green' : 'gray'}
        size="2"
        aria-label="Include other opted-in DISCOs"
        onClick={handleClick}
      >
        <LuGlobe size={15} />
      </IconButton>
    </Tooltip>
  )
}
